import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    Documents,
    EfeAccordionValues,
    EfeResponse,
    Field,
    IdentificationServiceRequest,
    PersonalDataServiceRequest,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {
    Constants,
    HistoryRoutes,
    IMAGE_PREFIX, IMAGE_PREFIX_PNG,
    INE_ID,
    Messages,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {ClientService, DocumentServices, ValidationsService} from 'services';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import DocsIdForm from './docs-id-form/DocsIdForm';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import {nextStep, getBase64Extension, blobToBase64} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {
    OrcValidations,
    ValidationsINERequest,
} from 'commons/models/dtos/validations/Validations';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import ForeingClients from 'services/clients/ForeingClients';
import {validateOcrResponse} from 'commons/utils/DniValidationFunctions';

const errorDetail = {
    data: {
        process: Messages.IDENTIFICATION_SECTION,
        message: ' ',
        errors: [''],
    },
};

function DocsIdentification() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {enqueueSnackbar} = useSnackbar();
    const {menu, expedientId, fatherKycId, foreign} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [docsIdentification, setDocsIdentification] = React.useState<any>(undefined);
    const [personaldata, setPersonalData] = React.useState<PersonalDataServiceRequest>();
    const [editing, setEditing] = React.useState<boolean>(false);
    
    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.DATA_DOCUMENTATION_ID === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const personalInfo = await getPersonalData(fatherKycId);
                    console.log('personalInfo', personalInfo);
                    if (!personalInfo) {
                        setDocsIdentification(undefined);
                        history.push(HistoryRoutes.DOCS_INFO);
                        enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                            variant: SnackbarVariants.ERROR,
                        });
                    } else if (!personalInfo.name) {
                        history.push(HistoryRoutes.DOCS_INFO);
                        setDocsIdentification(undefined);
                        enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                            variant: SnackbarVariants.ERROR,
                        });
                    }
                    setPersonalData(personalInfo);
                    const response: EfeResponse<IdentificationServiceRequest> =
                        await ClientService.getIdentificationData(fatherKycId);
                    if (response && response.data) {
                        setIsLoading(true);
                        try {
                            const documentResponse: EfeResponse<Documents> =
                            await DocumentServices.getDocumentByKycId(
                                Constants.DOC_DNI_FRONT_CL,
                                expedientId,
                                fatherKycId
                            );
                            if (
                                documentResponse &&
                                documentResponse.data &&
                                documentResponse.data.data
                            ) {
                                const x = await blobToBase64(documentResponse.data.data);
                                response.data.dniFront = x.toString();
                            }
                        }catch (e) {
                            response.data.dniFront = '';
                        }
                        
                        try {
                            const ndiDocumentResponse: EfeResponse<Documents> =
                            await DocumentServices.getDocumentByKycId(
                                Constants.DOC_DNI_BACK_CL,
                                expedientId,
                                fatherKycId
                            );
                            if (
                                ndiDocumentResponse &&
                                ndiDocumentResponse.data &&
                                ndiDocumentResponse.data.data
                            ) {
                                const x = await blobToBase64(ndiDocumentResponse.data.data);
                                response.data.dniBack = x.toString();
                            }
                        } catch(e) {
                            response.data.dniBack = '';
                        }
                        setDocsIdentification(response.data);
                        setEditing(true);
                        setIsLoading(false);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setDocsIdentification(undefined);
                }
            })();
        }
    }, []);

    

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Identificaci\u00f3n Oficial',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <DocsIdForm
                            fields={fields}
                            submitHandler={onSubmit}
                            docsIdentification={docsIdentification}
                            foreign={foreign}
                            editing={editing}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.DATA_DOCUMENTATION_ID === flow.data.expandKey,
        },
    ];

    async function saveDniFront(values: IdentificationServiceRequest) {
        if (values.dniFront) {
            const extension = getBase64Extension(values.dniFront);
            const fileConverted = await (await fetch(values.dniFront)).blob();
            const document: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_DNI_FRONT_CL,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_DNI_FRONT_CL}.${extension}`,
            };

            const documentResponse: EfeResponse<any> = await DocumentServices.updateDocument(
                document
            );
            return documentResponse;
        }
    }

    async function getPersonalData(kycId: number): Promise<PersonalDataServiceRequest | undefined> {
        let personalInfo: EfeResponse<PersonalDataServiceRequest> | undefined = undefined;
        try {
            if (foreign) {
                personalInfo = await ForeingClients.getPersonalData(kycId);
            } else {
                personalInfo = await ClientService.getClientPersonalData(kycId);
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                variant: SnackbarVariants.ERROR,
            });
            history.push(HistoryRoutes.DOCS_INFO);
        }

        return personalInfo ? personalInfo.data : undefined;
    }

    async function saveDniBack(values: IdentificationServiceRequest) {
        if (values.dniBack) {
            const extension = getBase64Extension(values.dniBack);
            const fileConverted = await (await fetch(values.dniBack)).blob();
            const document: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_DNI_BACK_CL,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_DNI_BACK_CL}.${extension}`,
            };

            const documentResponse: EfeResponse<any> = await DocumentServices.updateDocument(
                document
            );
            return documentResponse;
        }
    }

    async function validateDni(values: IdentificationServiceRequest): Promise<OrcValidations> {
        const validation: OrcValidations = {
            success: false,
            errors: [],
        };
        if (foreign) {
            await saveDniFront(values);
            await saveDniBack(values);
            validation.success = true;
            return validation;
        } else if (values && values.dniBack && values.dniFront) {
            const dniBack = values.dniBack.replace(IMAGE_PREFIX, '').replace(IMAGE_PREFIX_PNG, '');
            const dniFront = values.dniFront.replace(IMAGE_PREFIX, '').replace(IMAGE_PREFIX_PNG, '');
            const request: ValidationsINERequest = {
                idReverso: dniBack,
                id: dniFront,
            };
            try {
                if (values.identificationType === INE_ID) {
                    const validationResponse = await ValidationsService.validateDni(request);
                    validation.response = validationResponse?.data;
                    if (personaldata && validationResponse && validationResponse.data) {
                        const ocrValidationsResponse = validateOcrResponse(
                            validationResponse.data,
                            personaldata
                        );
                        return ocrValidationsResponse;
                    }
                }
                validation.success = true;
                return validation;
            } catch (error: any) {
                if (error.code === 400) {
                    validation.errors = [Messages.DOCUMENT_WAS_NOT_IDENTIFIED];
                }
                return validation;
            }
        } else {
            return validation;
        }
    }

    async function onSubmit(values: IdentificationServiceRequest) {
        setIsLoading(true);
        try {
            const request = {...values};
            request.kycId = fatherKycId;
            request.dniBack = '';
            request.dniFront = '';
            const response: EfeResponse<IdentificationServiceRequest> =
                await ClientService.saveIdentificationData(request);
            if (response && response.data) {
                const valid = await validateDni(values);
                if (valid && valid.success) {
                    if (values.dniFront) {
                        await saveDniFront(values);
                    }
                    if (values.dniBack) {
                        await saveDniBack(values);
                    }
                    enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                    const path = nextStep(flow, menu);
                    if (Constants.MODULE_STEP !== path) {
                        setIsLoading(false);
                        history.push(path);
                    } else {
                        updateModuleStep(true);
                        flow.data.expandKey = path;
                        setIsLoading(false);
                    }
                } else {
                    setFields([...fields]);
                    errorDetail.data.errors = valid.errors;
                    showAlertError(errorDetail, '');
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => {
                    history.push(HistoryRoutes.DOCS_IDENTIFICATION);
                }}
                values={accordion}
            />
        </MainContent>
    );
}

export default DocsIdentification;
